import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import MarvinImageExporter from '@Services/MarvinImageExporter';
import StructureEditorInterface from '../StructureEditorInterface';
import { CDDOptions } from './ChemdrawConstants';

import './ChemdrawController.scss';

export default class ChemdrawStructureEditor extends StructureEditorInterface {
  /**
   * @ngdoc function
   * @name constructor
   * @description constructor
   */
  constructor(container) {
    super();
    this._instance = null;
    this.state = this._ready(container);
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _ready
   * @param {HTMLElement} container
   * @returns {Promise}
   * @description initialize Chemdraw editor in DOM
   */
  _ready(container) {
    return new Promise((resolve, reject) => {
      window.perkinelmer.ChemdrawWebManager.attach({
        element: container,
        configUrl: CDDOptions.configUrls.default,
        licenseUrl: CDDOptions.licenseUrl,
        viewonly: false,
        callback: (chemdraw) => {
          this._instance = chemdraw;
          if (this._instance && isFunction(this._instance.setContentChangedHandler)) {
            this._instance.setContentChangedHandler(async () => {
              if (isFunction(this.onChangeCallback)) {
                const isEditorEmpty = await this.isEmpty();
                this.onChangeCallback(isEditorEmpty);
              }
            });
          }
          if (isFunction(this.onLoadedCallback)) {
            this.onLoadedCallback();
          }
          resolve(chemdraw);
        },
        errorCallback: (error) => {
          Logger.error('ChemDraw JS attach error: ', error);
          reject(error);
        },
      });
    });
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _loadMol
   * @param {String} data
   * @returns {Promise}
   * @description load mol data into Chemdraw editor
   */
  _loadMol(data) {
    return new Promise((resolve, reject) => {
      if (this._instance) {
        this._instance.loadMOL(data, async (result, error) => {
          if (result) {
            resolve(result);
          } else if (error) {
            reject(error);
          }

          // Trigger onChangeCallback after structure was loaded into editor
          if (isFunction(this.onChangeCallback)) {
            const isEditorEmpty = await this.isEmpty();
            this.onChangeCallback(isEditorEmpty);
          }
        });
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject('Missing Chemdraw instance');
      }
    });
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _export
   * @returns {Promise}
   * @description get mol data from Chemdraw editor
   */
  _export() {
    return new Promise((resolve, reject) => {
      if (this._instance) {
        // NOTE: this requires WebService to be enabled in configuration.json
        this._instance.findReactions((reactions, error) => {
          let parsedReactions;

          // for some reason reactions are stringified json...
          try {
            parsedReactions = JSON.parse(reactions);
          } catch (err) {
            reject(error);
            return;
          }

          const molCallback = (molResult, molError) => {
            if (molResult) {
              resolve(molResult);
            } else if (molError) {
              reject(molError);
            }
          };

          // if found reactions are not empty then consider it a reaction, otherwise consider it a substance
          if (!isEmpty(parsedReactions)) {
            this._instance.getRXNV3000(molCallback);
          } else {
            this._instance.getMOLV3000(molCallback);
          }
        });
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject('Missing Chemdraw instance');
      }
    });
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name _preview
   * @param {String} moldData
   * @returns {Promise}
   * @description get preview image, using Marvin helper
   */
  // eslint-disable-next-line class-methods-use-this
  _preview(moldData) {
    return MarvinImageExporter.render({ data: moldData });
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name dispose
   * @description dispose current editor instance
   */
  dispose() {
    if (this._instance) {
      this._instance.dispose();
    }
  }

  // ------- Shared API -------

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name getData
   * @returns {Promise}
   * @description get current structure data, including mol data and preview image
   */
  getData() {
    return this.state
      .then(() => {
        return this._export()
          .then((molData) => {
            return this._preview(molData)
              .then((img) => {
                return {
                  data: molData,
                  preview: img,
                };
              })
              .catch(Logger.reject);
          })
          .catch(Logger.reject);
      })
      .catch(Logger.reject);
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name loadStructure
   * @param {String} structure
   * @returns {Promise}
   * @description load structure (mold data) into current editor
   */
  loadStructure(structure) {
    return this.state.then(() => this._loadMol(structure)).catch(Logger.reject);
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name isEmpty
   * @returns {Promise}
   * @description check if current editor is empty
   */
  isEmpty() {
    return Promise.resolve(this._instance && this._instance.isBlankStructure());
  }

  /**
   * @ngdoc method
   * @methodOf app.structure-editor.chemdraw:ChemdrawStructureEditor
   * @name clear
   * @description clears current editor
   */
  clear() {
    if (this._instance) {
      this._instance.clear();
    }
  }

  onChange(callback) {
    this.onChangeCallback = callback;
  }

  onLoaded(callback) {
    this.onLoadedCallback = callback;
  }
}
